import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as QueryString from "query-string";
import { saveAuthState } from "../../helpers/localStorage";
import { Views } from "../../helpers/constants";
import { loginServices } from "../../services/login.services";
import { StorageKeys } from "../../services/constants";
import { getClientID, logout, updateLocalToken } from "./loginSlice";
import { useAppDispatch } from "../app/appHooks";
import { firebaseLogin } from "../app/asyncThunkActions";
import { getHeaderInformation } from "../header/headerThunks";

const clearUserScopedStorage = () => {
  localStorage.removeItem(StorageKeys.SELECTED_COMMUNITY_ID);
  localStorage.removeItem(StorageKeys.SELECTED_ORGANIZATION_ID);
  localStorage.removeItem(StorageKeys.ALL_NOTIFICATIONS);
};

const getStoredUserID = () => {
  const authState = localStorage.getItem(StorageKeys.USER);
  if (!authState) return undefined;

  try {
    return JSON.parse(authState).user_id as string | undefined;
  } catch {
    return undefined;
  }
};

// Handles the SSO callback by exchanging an authorization code for tokens,
// storing them locally, and hydrating Redux before navigating to the dashboard.
export default function SSOCallback() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const hasExchangedCode = useRef(false);

  useEffect(() => {
    if (hasExchangedCode.current) return;
    hasExchangedCode.current = true;

    const params = QueryString.parse(location.search);
    const code = params.code as string;
    const clientID = (params.client_id as string | undefined) || getClientID();

    const handleSSOCallback = async () => {
      if (!code) {
        history.replace(Views.LOGIN);
        return;
      }

      try {
        const storedUserID = getStoredUserID();
        const token = await loginServices.exchangeSSOCode(code, clientID);

        if (storedUserID !== token.user_id) {
          saveAuthState(undefined);
          clearUserScopedStorage();
          dispatch(logout());
          try {
            await loginServices.logoutFirebase();
          } catch (err) {
            // Continue the new SSO login attempt even if no Firebase user was active.
          }
        }

        const firebaseLoginResult = await dispatch(
          firebaseLogin(token.access_token)
        );
        if (firebaseLogin.rejected.match(firebaseLoginResult)) {
          throw firebaseLoginResult.error;
        }
        if (
          firebaseLogin.fulfilled.match(firebaseLoginResult) &&
          !firebaseLoginResult.payload
        ) {
          throw new Error("Firebase login failed");
        }
        saveAuthState(token);
        dispatch(updateLocalToken(token));
        await dispatch(getHeaderInformation());
        history.replace(Views.DASHBOARD);
      } catch (err) {
        history.replace(Views.LOGIN);
      }
    };

    handleSSOCallback();
  }, [dispatch, history, location.search]);

  return null;
}
